import { useEffect, useState } from "react";
import { Check, FileJson, RefreshCw, Save } from "lucide-react";
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">{children}</div>
);

const InputBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";
const TextAreaBase =
  "w-full min-h-[120px] rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const defaultForm = {
  title: "",
  description: "",
  canonical_url: "",
  robots: "index,follow",
  og_title: "",
  og_description: "",
  og_image: "",
  og_type: "",
  twitter_card: "",
  twitter_title: "",
  twitter_description: "",
  twitter_image: "",
  json_ld: "",
};

const buildJson = (value) => {
  if (!value || !String(value).trim()) return null;
  return JSON.parse(String(value));
};

export default function SeoMetaGlobal() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState({ og: false, twitter: false });
  const [imageHints, setImageHints] = useState({ og: "", twitter: "" });

  const loadGlobal = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/seo-meta/global");
      const data = res.data?.data || null;
      if (!data) {
        setForm(defaultForm);
        return;
      }
      setForm({
        title: data.title || "",
        description: data.description || "",
        canonical_url: data.canonical_url || "",
        robots: data.robots || "index,follow",
        og_title: data.og_title || "",
        og_description: data.og_description || "",
        og_image: data.og_image || "",
        og_type: data.og_type || "",
        twitter_card: data.twitter_card || "",
        twitter_title: data.twitter_title || "",
        twitter_description: data.twitter_description || "",
        twitter_image: data.twitter_image || "",
        json_ld: data.json_ld ? JSON.stringify(data.json_ld, null, 2) : "",
      });
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Failed to load metadata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGlobal();
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateJsonLd = (value) => {
    if (!value || !String(value).trim()) {
      setJsonError("");
      return true;
    }
    try {
      JSON.parse(String(value));
      setJsonError("");
      return true;
    } catch (err) {
      setJsonError("JSON-LD must be valid JSON");
      return false;
    }
  };

  const handlePrettify = () => {
    if (!form.json_ld || !String(form.json_ld).trim()) return;
    try {
      const parsed = JSON.parse(String(form.json_ld));
      updateField("json_ld", JSON.stringify(parsed, null, 2));
      setJsonError("");
    } catch {
      setJsonError("JSON-LD must be valid JSON to prettify");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");

    const jsonOk = validateJsonLd(form.json_ld);
    if (!jsonOk) return;

    const payload = {
      ...form,
      json_ld: buildJson(form.json_ld),
    };

    setSaving(true);
    try {
      await API.put("/seo-meta/global", payload);
      setStatus("Global SEO metadata saved.");
      await loadGlobal();
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const validateImageFile = async (file, target) => {
    if (!file) return;
    const messages = [];
    const sizeKb = Math.round((file.size || 0) / 1024);
    if (sizeKb > 500) {
      messages.push(`Recommended < 500KB (current: ${sizeKb}KB).`);
    }
    if (!/image\/(jpeg|png)/.test(file.type || "")) {
      messages.push("Only JPG or PNG images are recommended.");
    }

    const url = URL.createObjectURL(file);
    try {
      const info = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => reject(new Error("Invalid image"));
        img.src = url;
      });

      if (info.width !== 1200 || info.height !== 630) {
        messages.push(`Recommended size 1200x630 (current: ${info.width}x${info.height}).`);
      }
    } catch {
      messages.push("Could not read image dimensions.");
    } finally {
      URL.revokeObjectURL(url);
    }

    setImageHints((prev) => ({
      ...prev,
      [target]: messages.join(" "),
    }));
  };

  const uploadImage = async (file, target) => {
    if (!file) return;
    setError("");
    setStatus("");
    await validateImageFile(file, target);

    const formData = new FormData();
    formData.append("file", file);

    setUploading((prev) => ({ ...prev, [target]: true }));
    try {
      const res = await API.post(`/seo-meta/global/upload-image?target=${target}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.data?.url || res.data?.data?.path;
      if (url) {
        updateField("og_image", url);
        updateField("twitter_image", url);
        setStatus("Image uploaded. Remember to save changes.");
      } else {
        setError("Upload succeeded but no URL was returned.");
      }
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Image upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, [target]: false }));
    }
  };

  return (
    <AdminPageShell
      title="SEO/Metadata (cherrfy.com)"
      description="Edit the site-wide default metadata used across the Cherrfy storefront."
      actions={
        <>
          <SecondaryButton type="button" onClick={loadGlobal} disabled={loading}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </SecondaryButton>
          <PrimaryButton type="submit" form="seo-meta-global-form" disabled={saving || loading}>
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </>
      }
    >
      <form id="seo-meta-global-form" onSubmit={handleSubmit} className="space-y-6">
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        ) : null}
        {status ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 inline-flex items-center gap-2">
            <Check className="h-4 w-4" />
            {status}
          </div>
        ) : null}

        <Card>
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Basic SEO</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Title, description, and indexing directives.</p>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Title</label>
              <input value={form.title} onChange={(e) => updateField("title", e.target.value)} className={InputBase} />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Canonical URL</label>
              <input
                value={form.canonical_url}
                onChange={(e) => updateField("canonical_url", e.target.value)}
                className={InputBase}
                placeholder="https://..."
              />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                className={TextAreaBase}
                rows={4}
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Robots</label>
              <input
                value={form.robots}
                onChange={(e) => updateField("robots", e.target.value)}
                className={InputBase}
                placeholder="index,follow"
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Open Graph</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Preview metadata for social sharing.</p>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">OG Title</label>
              <input value={form.og_title} onChange={(e) => updateField("og_title", e.target.value)} className={InputBase} />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">OG Type</label>
              <input value={form.og_type} onChange={(e) => updateField("og_type", e.target.value)} className={InputBase} placeholder="website/product/article" />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">OG Description</label>
              <textarea value={form.og_description} onChange={(e) => updateField("og_description", e.target.value)} className={TextAreaBase} rows={3} />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">OG Image URL</label>
              <input value={form.og_image} onChange={(e) => updateField("og_image", e.target.value)} className={InputBase} placeholder="https://..." />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => uploadImage(e.target.files?.[0], "og")}
                  className="text-xs text-slate-600"
                />
                {uploading.og ? (
                  <span className="text-xs text-slate-500">Uploading...</span>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Recommended: 1200x630 JPG/PNG, under 500KB.
              </p>
              {imageHints.og ? <p className="mt-1 text-xs text-amber-600">{imageHints.og}</p> : null}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Twitter</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Twitter card metadata.</p>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Twitter Card</label>
              <input value={form.twitter_card} onChange={(e) => updateField("twitter_card", e.target.value)} className={InputBase} placeholder="summary_large_image" />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Twitter Title</label>
              <input value={form.twitter_title} onChange={(e) => updateField("twitter_title", e.target.value)} className={InputBase} />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Twitter Description</label>
              <textarea value={form.twitter_description} onChange={(e) => updateField("twitter_description", e.target.value)} className={TextAreaBase} rows={3} />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Twitter Image URL</label>
              <input value={form.twitter_image} onChange={(e) => updateField("twitter_image", e.target.value)} className={InputBase} placeholder="https://..." />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => uploadImage(e.target.files?.[0], "twitter")}
                  className="text-xs text-slate-600"
                />
                {uploading.twitter ? (
                  <span className="text-xs text-slate-500">Uploading...</span>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Recommended: 1200x630 JPG/PNG, under 500KB.
              </p>
              {imageHints.twitter ? <p className="mt-1 text-xs text-amber-600">{imageHints.twitter}</p> : null}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Schema (JSON-LD)</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600">Optional schema override for advanced SEO markup.</p>

          <div className="mt-4">
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">JSON-LD</label>
            <textarea
              value={form.json_ld}
              onChange={(e) => {
                updateField("json_ld", e.target.value);
                validateJsonLd(e.target.value);
              }}
              className={cx(TextAreaBase, "min-h-[200px] font-mono")}
              placeholder='{"@context": "https://schema.org", "@type": "WebSite"}'
            />
            {jsonError ? <p className="mt-2 text-xs text-rose-600">{jsonError}</p> : null}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <SecondaryButton type="button" onClick={handlePrettify}>
                <FileJson className="h-4 w-4" />
                Prettify JSON
              </SecondaryButton>
              <SecondaryButton type="button" onClick={() => updateField("json_ld", "")}>
                <RefreshCw className="h-4 w-4" />
                Clear
              </SecondaryButton>
            </div>
          </div>
        </Card>
      </form>
    </AdminPageShell>
  );
}
